import 'regenerator-runtime/runtime';
import React from 'react';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './index.css';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import registerServiceWorker from 'pwa/helpers/registerServiceWorker';
import App from './App';
// eslint-disable-next-line import/no-named-as-default
import reportWebVitals from './reportWebVitals';
import './i18n';

Sentry.init({
  dsn: `${import.meta.env.REACT_APP_SENTRY_DSN}`,
  environment: `${import.meta.env.REACT_APP_SENTRY_ENVIRONMENT}`,
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: false,
    }),
  ],
});

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Failed to find the root element');
const root = createRoot(rootElement);

const isPosthogEnabled = import.meta.env.REACT_APP_POSTHOG_ENABLED === 'true';

if (isPosthogEnabled) {
  posthog.init(import.meta.env.REACT_APP_POSTHOG_API_KEY || '', {
    api_host: import.meta.env.REACT_APP_POSTHOG_HOST || '',
    person_profiles: 'identified_only',
    session_recording: {
      maskTextSelector: '*',
    },
  });
}

registerServiceWorker();

const queryClient = new QueryClient();

const children = <App />;

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <GoogleReCaptchaProvider
        reCaptchaKey={import.meta.env.REACT_APP_RECAPTCHA_KEY || ''}
      >
        {isPosthogEnabled ? (
          <PostHogProvider client={posthog}>{children}</PostHogProvider>
        ) : (
          children
        )}
      </GoogleReCaptchaProvider>
    </QueryClientProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
